//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

// #region Imports
import { EthAddr, TxHash } from '@shared/SchemaTypes'
import { Contract, Signer } from 'ethers'

import { ActivityEnum, RoleEnum } from '@shared/Enums'

import { poolActions } from '@react/redux/poolSlice'

import { logOT } from '@shared/loggingShared'

import { ThunkAction, UnknownAction } from '@reduxjs/toolkit'

import { assertRole, dispatchError, OtStoreState } from '@react/redux/store'

import { ERC20Wei, otAssert } from '@shared/conversions'

import { PoolAbi } from '@shared/Abis'
import { txUUID, wsServerActionPoolTX, wsServerActionPoolTXRequest } from '@shared/WsServerActions'

export const repayEarlyWithdrawTx =
  (props: {
    poolAddr: EthAddr
    lenderAddr: EthAddr
    amount: ERC20Wei
  }): ThunkAction<void, OtStoreState, unknown, UnknownAction> =>
  async (dispatch, getState): Promise<void> => {
    const activity = ActivityEnum.WITHDRAW_REQUEST

    try {
      const { webSocket, userConnection } = getState().chainState
      const { chainConfigurationName } = getState().chainState.chainConfiguration
      otAssert(userConnection, 'userConnection is not defined')
      const { roleEnum, signer } = userConnection
      assertRole(roleEnum, RoleEnum.BORROWER)
      const { poolAddr, lenderAddr, amount } = props

      logOT.debug('repayEarlyWithdraw', poolAddr)
      await repayEarlyWithdraw(webSocket, signer as Signer, poolAddr, lenderAddr, amount, chainConfigurationName)
      dispatch(poolActions.repayEarlyWithdrawSuccess(props))
    } catch (e: any) {
      dispatch(poolActions.repayEarlyWithdrawFail(props))
      await dispatchError(dispatch, e, activity, 1017)
    }
  }

export const repayEarlyWithdraw = async (
  webSocket: WebSocket | undefined,
  borrowerSigner: Signer,
  poolAddr: EthAddr,
  lenderAddr: EthAddr,
  amount: ERC20Wei,
  chainConfigurationName: string
): Promise<void> => {
  logOT.debug('repayEarlyWithdraw', poolAddr, lenderAddr, amount)

  const uuid = txUUID()
  webSocket?.send(wsServerActionPoolTXRequest(ActivityEnum.WITHDRAW_REQUEST, poolAddr, uuid, chainConfigurationName))
  const poolContract = new Contract(poolAddr, PoolAbi, borrowerSigner)

  const tx = await poolContract.repayEarlyWithdraw(lenderAddr, amount)

  webSocket?.send(
    wsServerActionPoolTX(ActivityEnum.WITHDRAW_REQUEST, poolAddr, uuid, tx.hash as TxHash, chainConfigurationName)
  )
  await tx.wait(1)

  logOT.debug('repayEarlyWithdraw tx', tx)
}
